import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, FileText, Download, CheckCircle2, RefreshCw, Trash2, FileArchive, Eye } from "lucide-react";
import api from "@/lib/api";
import { chf, moisCourant, periodeLabel, MOIS_FR, STATUT_FICHE } from "./salairesUtils";

async function telecharger(url, body, filename, type = "application/pdf") {
  const { data } = body
    ? await api.post(url, body, { responseType: "blob" })
    : await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export default function SalairesFichesTab() {
  const [periode, setPeriode] = useState(moisCourant());
  const [fiches, setFiches] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetch = useCallback(async (annule = () => false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/salaires/fiches", { params: { periode } });
      if (annule()) return;
      setFiches(data.items || []);
      setTotaux(data.totaux);
    } finally { if (!annule()) setLoading(false); }
  }, [periode]);

  useEffect(() => {
    let annule = false;
    fetch(() => annule);
    return () => { annule = true; };
  }, [fetch]);

  const generer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/salaires/fiches/generer", { periode, recalculer: true });
      toast.success(`${data.fiches.length} fiche(s) calculée(s) pour ${periodeLabel(periode)}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const action = async (f, verbe, url) => {
    try {
      await api.post(url);
      toast.success(`Fiche ${verbe}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const supprimer = async (f) => {
    try {
      await api.delete(`/salaires/fiches/${f.id}`);
      toast.success("Fiche supprimée");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="salaires-fiches">
      <Card className="p-4 border-gray-200">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Période</Label>
            <Input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)}
                   className="mt-1 w-44" data-testid="fiches-periode" />
          </div>
          <Button onClick={generer} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="fiches-generer">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Calculer les fiches du mois
          </Button>
          {totaux && fiches.length > 0 && (
            <div className="text-xs text-gray-600 pb-2">
              Brut <b className="font-mono">{chf(totaux.brut)}</b> ·
              Net <b className="font-mono"> {chf(totaux.a_verser)}</b> ·
              Coût employeur <b className="font-mono"> {chf(totaux.cout_total)}</b>
            </div>
          )}
        </div>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : fiches.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">
            Aucune fiche pour {periodeLabel(periode)} — cliquez sur « Calculer les fiches du mois »
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Collaborateur</th>
                  <th className="px-3 py-2 text-right">Brut</th>
                  <th className="px-3 py-2 text-right">Déductions</th>
                  <th className="px-3 py-2 text-right">Net versé</th>
                  <th className="px-3 py-2 text-right">Coût employeur</th>
                  <th className="px-3 py-2">Statut</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="fiches-table">
                {fiches.map((f) => {
                  const c = f.calcul || {};
                  const emp = f.employe_snapshot || {};
                  const st = STATUT_FICHE[f.statut] || STATUT_FICHE.brouillon;
                  return (
                    <tr key={f.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-gray-900">{emp.prenom} {emp.nom}</div>
                        <div className="text-[11px] text-gray-500">{f.numero}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{chf(c.brut_total)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-600">{chf(c.total_retenues)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold">{chf(c.a_verser)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-600">{chf(c.cout_total_employeur)}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                        {(c.alertes || []).length > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5">{c.alertes.join(" · ")}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setDetail(f)} data-testid={`fiche-detail-${f.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Bulletin PDF"
                                onClick={() => telecharger(`/salaires/fiches/${f.id}/pdf`, null, `bulletin-${f.periode}-${emp.nom}.pdf`)}
                                data-testid={`fiche-pdf-${f.id}`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        {f.statut === "brouillon" && (
                          <>
                            <Button size="sm" variant="ghost" title="Valider"
                                    onClick={() => action(f, "validée", `/salaires/fiches/${f.id}/valider`)}
                                    data-testid={`fiche-valider-${f.id}`}>
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Supprimer" onClick={() => supprimer(f)}
                                    data-testid={`fiche-supprimer-${f.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {f.statut === "validee" && (
                          <Button size="sm" variant="ghost" title="Repasser en brouillon"
                                  onClick={() => action(f, "repassée en brouillon", `/salaires/fiches/${f.id}/devalider`)}
                                  data-testid={`fiche-devalider-${f.id}`}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <GenerationGroupee />

      {detail && <FicheDetailDialog fiche={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function GenerationGroupee() {
  const annee = new Date().getFullYear();
  const [an, setAn] = useState(annee);
  const [mois, setMois] = useState([new Date().getMonth() + 1]);
  const [employes, setEmployes] = useState([]);
  const [selEmp, setSelEmp] = useState([]);
  const [tri, setTri] = useState("mois");
  const [apercu, setApercu] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/salaires/employes", { params: { inclure_archives: true } })
      .then(({ data }) => setEmployes(data.items || [])).catch(() => {});
  }, []);

  const body = () => ({
    annee: Number(an), mois, tri,
    employe_ids: selEmp.length ? selEmp : null,
    generer_si_absent: false,
  });

  const voirApercu = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/salaires/fiches/apercu", body());
      setApercu(data);
      if (!data.nb_fiches) toast.info("Aucune fiche existante pour cette sélection");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const dl = async (type) => {
    setBusy(true);
    try {
      if (type === "pdf") await telecharger("/salaires/fiches/pdf-groupe", body(), `bulletins-${an}.pdf`);
      if (type === "zip") await telecharger("/salaires/fiches/zip", body(), `bulletins-${an}.zip`, "application/zip");
      if (type === "recap") await telecharger("/salaires/recapitulatif/pdf", body(), `recapitulatif-${an}.pdf`);
      toast.success("Document généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aucune fiche pour cette sélection");
    } finally { setBusy(false); }
  };

  const toggleMois = (m) => setMois((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m].sort((a, b) => a - b));
  const toggleEmp = (id) => setSelEmp((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <Card className="p-4 border-gray-200" data-testid="generation-groupee">
      <h3 className="font-heading font-semibold text-gray-900 mb-1">Génération groupée de bulletins</h3>
      <p className="text-xs text-gray-500 mb-4">
        Choisissez une année, un ou plusieurs mois et un ou plusieurs collaborateurs — même non consécutifs.
      </p>

      <div className="grid lg:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">Année</Label>
          <Input type="number" value={an} onChange={(e) => setAn(e.target.value)} className="mt-1 w-32" data-testid="groupe-annee" />
          <Label className="text-xs mt-3 block">Mois</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {MOIS_FR.slice(1).map((m, i) => (
              <button key={m} type="button" onClick={() => toggleMois(i + 1)}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                        mois.includes(i + 1)
                          ? "bg-[#52a675] text-white border-[#52a675]"
                          : "bg-white text-gray-600 border-gray-200 hover:border-[#52a675]"}`}
                      data-testid={`groupe-mois-${i + 1}`}>
                {m.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">Collaborateurs</Label>
          <div className="mt-1 max-h-44 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={selEmp.length === 0} onChange={() => setSelEmp([])}
                     className="rounded border-gray-300 text-[#52a675]" data-testid="groupe-tous-employes" />
              Tous les collaborateurs
            </label>
            {employes.map((e) => (
              <label key={e.id} className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={selEmp.includes(e.id)} onChange={() => toggleEmp(e.id)}
                       className="rounded border-gray-300 text-[#52a675]" data-testid={`groupe-emp-${e.id}`} />
                {e.prenom} {e.nom}
              </label>
            ))}
          </div>
          <Label className="text-xs mt-3 block">Tri</Label>
          <div className="flex gap-1.5 mt-1">
            {[["mois", "Par mois"], ["employe", "Par collaborateur"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setTri(v)}
                      className={`text-xs px-2.5 py-1 rounded-md border ${
                        tri === v ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200"}`}
                      data-testid={`groupe-tri-${v}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Button variant="outline" onClick={voirApercu} disabled={busy} className="w-full" data-testid="groupe-apercu">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Aperçu
          </Button>
          {apercu && (
            <div className="text-xs bg-gray-50 border border-gray-200 rounded-md p-2" data-testid="groupe-apercu-result">
              <div><b>{apercu.nb_fiches}</b> fiche(s) · {apercu.periodes.length} mois · {apercu.employes.length} collaborateur(s)</div>
              <div className="text-gray-500 mt-0.5">
                Brut {chf(apercu.totaux?.brut)} · Net {chf(apercu.totaux?.a_verser)}
              </div>
            </div>
          )}
          <Button onClick={() => dl("pdf")} disabled={busy} className="w-full bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="groupe-pdf">
            <Download className="w-4 h-4 mr-2" /> PDF unique (1 page / fiche)
          </Button>
          <Button onClick={() => dl("zip")} disabled={busy} variant="outline" className="w-full" data-testid="groupe-zip">
            <FileArchive className="w-4 h-4 mr-2" /> ZIP (fiches séparées)
          </Button>
          <Button onClick={() => dl("recap")} disabled={busy} variant="outline" className="w-full" data-testid="groupe-recap">
            <FileText className="w-4 h-4 mr-2" /> Récapitulatif de la période
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FicheDetailDialog({ fiche, onClose }) {
  const c = fiche.calcul || {};
  const emp = fiche.employe_snapshot || {};
  const Ligne = ({ l, m, mono = true, bold = false }) => (
    <div className={`flex justify-between py-1 border-b border-gray-100 ${bold ? "font-semibold" : ""}`}>
      <span className="text-gray-700">{l}</span>
      <span className={mono ? "font-mono" : ""}>{chf(m)}</span>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="fiche-detail-dialog">
        <DialogHeader>
          <DialogTitle>
            {emp.prenom} {emp.nom} — {periodeLabel(fiche.periode)}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-4">
          <div>
            <h4 className="text-xs uppercase text-gray-500 font-semibold mb-1">Rémunérations</h4>
            {(c.remunerations || []).map((r, i) => (
              <Ligne key={i} l={`${r.libelle}${r.detail ? ` · ${r.detail}` : ""}`} m={r.montant} />
            ))}
            <Ligne l="Salaire brut" m={c.brut_total} bold />
          </div>
          <div>
            <h4 className="text-xs uppercase text-gray-500 font-semibold mb-1">Déductions</h4>
            {(c.retenues || []).map((r, i) => (
              <Ligne key={i} l={`${r.libelle}${r.taux ? ` (${r.taux} %)` : ""}`} m={r.montant} />
            ))}
            <Ligne l="Total des déductions" m={c.total_retenues} bold />
          </div>
          <div>
            <h4 className="text-xs uppercase text-gray-500 font-semibold mb-1">Versement</h4>
            {(c.non_soumis || []).map((r, i) => <Ligne key={i} l={r.libelle} m={r.montant} />)}
            {!!c.avances && <Ligne l="Avance déjà versée" m={-c.avances} />}
            <Ligne l="Montant versé" m={c.a_verser} bold />
          </div>
          <div>
            <h4 className="text-xs uppercase text-gray-500 font-semibold mb-1">Charges patronales</h4>
            {(c.charges_employeur || []).map((r, i) => (
              <Ligne key={i} l={`${r.libelle}${r.taux ? ` (${r.taux} %)` : ""}`} m={r.montant} />
            ))}
            <Ligne l="Coût total employeur" m={c.cout_total_employeur} bold />
          </div>
          {c.lpp && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-2">
              LPP : salaire annuel de référence {chf(c.lpp.salaire_annuel_reference)} ·
              salaire assuré {chf(c.lpp.salaire_assure)} · taux {c.lpp.taux || 0} %
              {c.lpp.motif ? ` · ${c.lpp.motif}` : ""}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
